import * as styles from "./ImageEditor.css";

import { useRef, useState } from "react";
import type { JSX } from "react";
import { match } from "ts-pattern";

import { Button } from "@frontend/components/atoms/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@frontend/components/atoms/Dialog";
import { Icon } from "@frontend/components/atoms/Icon";
import { useLingui } from "@lingui/react/macro";

import { CameraCapture } from "./CameraCapture";
import { ImageCropper } from "./ImageCropper";
import type { ImageEditorProps, ImageEditorStep } from "./ImageEditor.types";
import { SourcePicker } from "./SourcePicker";

export function ImageEditor({
  children,
  onConfirm,
  aspect = 1,
}: ImageEditorProps): JSX.Element {
  const { t } = useLingui();

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<ImageEditorStep>("idle");
  const [imageSrc, setImageSrc] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const titles: Record<ImageEditorStep, string> = {
    idle: t`Edit the picture`,
    camera: t`Take a photo`,
    crop: t`Crop the picture`,
  };

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    setStep("idle");
    setImageSrc(null);
  };

  const handleFile = (file: File) => {
    const reader = new FileReader();

    reader.onload = () => {
      setImageSrc(reader.result as string);
      setStep("crop");
    };

    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    handleFile(file);
    e.target.value = "";
  };

  const handleCapture = (dataUrl: string) => {
    setImageSrc(dataUrl);
    setStep("crop");
  };

  const handleConfirm = (file: File) => {
    onConfirm(file);
    setOpen(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger
          render={<div style={{ display: "contents" }} />}
          nativeButton={false}
        >
          {children}
        </DialogTrigger>

        <DialogContent>
          <DialogHeader>
            <div className={styles.heading}>
              {step !== "idle" && (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t`Back`}
                  onClick={() => setStep("idle")}
                >
                  <Icon name="ChevronLeft" />
                </Button>
              )}
              <DialogTitle>{titles[step]}</DialogTitle>
            </div>
            <DialogClose />
          </DialogHeader>

          {match(step)
            .with("idle", () => (
              <SourcePicker
                onFile={() => fileInputRef.current?.click()}
                onDrop={handleFile}
                onCamera={() => setStep("camera")}
              />
            ))
            .with("camera", () => <CameraCapture onCapture={handleCapture} />)
            .with(
              "crop",
              () =>
                imageSrc && (
                  <ImageCropper
                    imageSrc={imageSrc}
                    aspect={aspect}
                    onConfirm={handleConfirm}
                  />
                ),
            )
            .exhaustive()}
        </DialogContent>
      </Dialog>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleFileChange}
      />
    </>
  );
}
